<template>
  <div class="project-page">
    <Notification
      ref="notification"
      v-model="notificationData.active"
      :title="$t(notificationData.title)"
      :icon="notificationData.icon"
      :message="$t(notificationData.content, notificationData.contentParams)"
      @close="notificationData.active = false"
    />

    <div v-if="projectData" class="container">
      <div class="project-page__slider">
        <PagesProjectSlider
          :covers="projectData.covers"
          :project-name="projectData.name"
          :finished="finished"
        />
      </div>
      <div class="project-page__donate-info">
        <div class="project-page__donate-info--wrapper">
          <PagesProjectDonateInfo
            :project="projectData"
            :finished="finished"
            :locale="activeLanguage.value"
            @open-donate-modal="openDonateModal"
          />

          <PagesProjectDonateModal
            v-if="!finished"
            ref="donate-modal"
            :project="projectData"
            :referrer="user?.shortId"
            :utm-data="utmData"
            :update-project="updateProject"
            :open-promo-error="openPromoError"
            :open-payment-error="openPaymentError"
          />
        </div>
      </div>
      <div ref="description-section" class="project-page__description">
        <PagesProjectDescription
          v-if="projectData.description"
          :content="projectData.description"
        />

        <PagesProjectDocumentList
          v-if="documents.length"
          :documents="documents"
        />

        <PagesProjectParticipantsList
          v-if="participants?.length"
          :participants="participants"
          :participants-count="participantsCount"
          :locale="activeLanguage.value"
          :project-id="projectData.id"
        />
      </div>
    </div>

    <div v-else-if="projectError" class="error-block">
      <template v-if="projectError.code">
        <div class="error-block__code">{{ projectError.code }}</div>
        <div class="error-block__title">{{ projectError.error_title }}</div>
        <div class="error-block__message">{{ projectError.error_message }}</div>
      </template>

      <div v-else class="error-block__message">
        {{ projectError }}
      </div>
    </div>
  </div>
</template>

<script setup>
import camelcaseKeys from 'camelcase-keys';

import projectDefaultCover from '~/assets/images/project/projectDefaultCover.jpg';
import errorGift from '~/assets/icons/errorGift.svg';

import cleanSeoText from '~/utils/cleanSeoText';

import useLanguageStore from '~/store/language';
import useAnalyticsStore from '~/store/analytics';

const { $useApi } = useNuxtApp();

// Props
const { id: projectId } = defineProps({
  id: {
    type: String,
    required: true,
  },
  user: {
    type: Object,
    default: () => ({}),
  },
  utmData: {
    type: Object,
    default: null,
  },
});

const donateModal = useTemplateRef('donate-modal');
const error = ref(null);
const notificationData = ref({
  title: '',
  content: '',
  contentParams: {},
  active: false,
});
const activeLanguage = computed(() => useLanguageStore().getActiveLanguageCode);

const headers = computed(() => useCustomRequestHeaders());
const projectKey = computed(
  () => `projectData-${activeLanguage.value}-${projectId}`,
);

const {
  data: projectData,
  error: projectError,
  refresh: refreshProject,
} = await useFetch($useApi.project.getProject(projectId), {
  key: projectKey,
  headers,
  transform: (response) => camelcaseKeys(response, { deep: true }),
  onResponseError({ response }) {
    handleError(response);
  },
});

const { data: projectDocuments } = await useFetch(
  $useApi.project.getDocuments(projectData.value?.id),
  {
    key: `projectDocuments-${projectData.value?.id}`,
    headers: headers.value,
    transform: (response) => camelcaseKeys(response, { deep: true }),
    onResponseError({ response }) {
      handleError(response);
    },
  },
);

const { data: projectParticipants, refresh: refreshParticipants } =
  await useFetch($useApi.project.getParticipants, {
    key: `projectParicipants-${projectData.value?.id}`,
    query: { limit: 5, project_id: projectData.value?.id },
    headers: headers.value,
    transform: (response) => camelcaseKeys(response, { deep: true }),
    onResponseError({ response }) {
      handleError(response);
    },
  });

function handleError(err) {
  error.value = {
    status: err.status,
    statusText: err.statusText,
  };
}

// Computed
const finished = computed(
  () => projectData.value?.state === 'finished' || false,
);
const documents = computed(() => projectDocuments.value?.files || []);

const participants = computed(
  () => projectParticipants?.value?.participants || [],
);
const participantsCount = computed(
  () => projectParticipants?.value?.count || 0,
);

// Watchers
watch(projectData, (data) => {
  if (data) setSeoData();
});

setSeoData();

// Methods
function setSeoData() {
  if (!projectData.value) return;
  useSeoData().set({
    title: projectData.value.name,
    description: cleanSeoText(projectData.value.description),
    image: getProjectCover() || projectDefaultCover,
  });
}

function getProjectCover() {
  if (!projectData.value) return null;

  if (projectData.value.covers) {
    const covers = [...projectData.value.covers].filter(
      (cover) => cover.type === 'image' && cover.data,
    );

    if (covers.length) {
      return covers[0].imagePart?.preview || covers[0].data || null;
    }
  }

  const firstCover = projectData.value.covers[0];

  if (firstCover && firstCover.type === 'video') {
    return firstCover.videoPart.preview || null;
  }

  return null;
}

function openDonateModal() {
  if (donateModal.value && donateModal.value.open) donateModal.value?.open();
}

const hasTrackedViewContent = ref(false);
const viewContentInteractionEvents = [
  'scroll',
  'click',
  'touchstart',
];

function getFbqProjectPayload() {
  return {
    content_ids: [String(projectData.value.id)],
    currency: 'USD',
    num_items: 0,
    value: 0,
  };
}

function trackViewContentOnce() {
  if (hasTrackedViewContent.value || finished.value || !projectData.value?.id) {
    return;
  }

  hasTrackedViewContent.value = true;
  useAnalyticsStore().trackFbq('ViewContent', getFbqProjectPayload());
  removeViewContentListeners();
}

function addViewContentListeners() {
  viewContentInteractionEvents.forEach((eventName) => {
    window.addEventListener(eventName, trackViewContentOnce, {
      passive: true,
      capture: true,
    });
  });
}

function removeViewContentListeners() {
  viewContentInteractionEvents.forEach((eventName) => {
    window.removeEventListener(eventName, trackViewContentOnce, {
      capture: true,
    });
  });
}

onMounted(() => {
  if (!finished.value && projectData.value?.id) {
    addViewContentListeners();
  }
});

onBeforeUnmount(() => {
  removeViewContentListeners();
});

function updateProject() {
  setTimeout(() => {
    refreshProject();
    refreshParticipants();
  }, 1500);
}

function openPromoError() {
  notificationData.value.active = true;
  notificationData.value.title = 'notification.bonus_error_title';
  notificationData.value.content = 'notification.bonus_error_msg';

  notificationData.value.contentParams = {
    amount: `${profilePromoAmount.value / 100}`,
  };

  notificationData.value.icon = errorGift;

  profileStore.removePromotion();
}

function openPaymentError() {
  notificationData.value.active = true;
  notificationData.value.title = 'notification.payment_error_title';
  notificationData.value.content = 'notification.payment_error_msg';
  notificationData.value.contentParams = {};
  notificationData.value.icon = null;
  updateProject();
}
</script>

<style lang="scss">
@use '@styles/setup/variables.scss' as *;
@use '@styles/components/pages/project/index.scss' as *;

.error-block {
  width: 100%;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 54px;
  font-weight: 900;
  text-align: center;
  color: $color-1-accent;

  &__code {
    font-size: 100px;
    margin-bottom: 32px;
  }

  &__message {
    font-size: 32px;
    font-weight: 600;
  }
}
</style>
